import React, { FunctionComponent, MouseEventHandler } from 'react';
import { useLocation, useMatch } from 'react-router-dom';

import { MessageType } from '@savgroup-front-common/types';

import { SafeFormattedMessage } from '../../formatters';
import { useAutoScrollIntoView } from '../../hooks/useAutoScrollIntoView';

import {
  $Counter,
  $Indicator,
  $RouteTabLink,
  $RouteTabSize,
} from './RouteTabs.styles';

interface RouteTabsLinkProps {
  url: string;
  isActive?: boolean;
  count: number;
  hasIndicator?: boolean;
  indicatorLabel?: MessageType | string;
  dataTestId?: string;
  onClick?: MouseEventHandler<HTMLAnchorElement>;
  level: 1 | 2;
  isHidden?: boolean;
  name: string;
}

const RouteTabsLink: FunctionComponent<
  React.PropsWithChildren<RouteTabsLinkProps>
> = ({
  children,
  url,
  count,
  hasIndicator = false,
  indicatorLabel,
  dataTestId,
  onClick,
  level,
  isHidden,
  isActive,
  name,
}) => {
  const location = useLocation();
  const isActiveLocal = useMatch(`${url}/*`) ?? isActive;
  const ref = useAutoScrollIntoView<HTMLAnchorElement>(
    location.hash === `#${name}`,
  );

  return (
    <$RouteTabSize>
      <$RouteTabLink
        to={url}
        $isActive={!!isActiveLocal}
        $count={count}
        data-testid={`RouteTabs-${dataTestId}`}
        onClick={onClick}
        $level={level}
        $isHidden={isHidden}
        data-active={!!isActiveLocal}
        id={name}
        ref={ref}
      >
        {children}
        {hasIndicator && indicatorLabel && (
          <span className="sr-only">
            {SafeFormattedMessage(indicatorLabel)}
          </span>
        )}
      </$RouteTabLink>
      {count > 0 && <$Counter>{count}</$Counter>}
      {hasIndicator && (
        <$Indicator
          aria-hidden="true"
          data-testid={`RouteTabs-${dataTestId}-indicator`}
        />
      )}
    </$RouteTabSize>
  );
};

export default RouteTabsLink;
